import { useMemo } from "react";
import { Lock as LockIcon, MailCheck as MailCheckIcon, RefreshCw as RefreshCwIcon } from "@tamagui/lucide-icons";
import { isWeb } from "@tamagui/web";
import { Card, Display5, InfoRow, ResponsiveImage, YStack } from "@medbillai/ui";
import { GrantedLogo } from "../../../components/local-images";
import { FakeDoorMmfaStep } from "./FakeDoorMmfaStep";
import type { FakeDoorMmfaVariant, StepConfig } from "./types";
import { useFakeDoorMmfa } from "./useFakeDoorMmfa";
const getSteps = (variant: FakeDoorMmfaVariant, payerName: string): StepConfig[] => [{
  id: "success_linked",
  title: "Insurance successfully linked!",
  subtitle: "Personalized answers about your coverage, costs and care will be available in a few minutes.",
  content: <YStack alignItems="center" justifyContent="center">
        <ResponsiveImage src={{
      web: "/static/linkSuccess.png",
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
      native: require("../../../assets/linkSuccess.png")
    }} alt="Link success" width={isWeb ? 240 : 220} height={isWeb ? 240 : 220} />
      </YStack>,
  primary: {
    label: "Continue",
    action: "continue"
  }
}, {
  id: "claim_monitoring",
  title: "Enable claim monitoring",
  subtitle: "Get alerted when we detect claims, errors or overcharges.",
  content: <ResponsiveImage src={{
    web: "/static/billingErrorFound.png",
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
    native: require("../../../assets/billingErrorFound.png")
  }} alt="Billing error found" />,
  primary: {
    label: "Continue",
    action: "continue"
  },
  secondary: {
    label: "Skip for now",
    action: "skip"
  }
}, variant === "connect_existing" ? {
  id: "email_action",
  title: "Connect your email",
  subtitle: `${payerName} sends a one-time code each time we check for new claims. By connecting your email, Granted can handle those codes automatically — we only ever read emails from ${payerName}, nothing else.`,
  content: <ResponsiveImage src={{
    web: "/static/connectYourEmail.png",
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
    native: require("../../../assets/connectYourEmail.png")
  }} alt="Claim monitoring" />,
  primary: {
    label: "Connect email",
    action: "continue"
  },
  secondary: {
    label: "Skip for now",
    action: "skip"
  }
} : {
  id: "email_action",
  title: "Add a new email to catch every claim",
  subtitle: "Here’s how it works:",
  content: <Card size="large" flexDirection="column" gap="$sp2" paddingLeft="$sp2" variant="elevated">
            <InfoRow icon={MailCheckIcon} title="A dedicated email, just for claims" description="We generate an alias address for you (ex: jane.doe@grantedemail.com)." />
            <InfoRow icon={RefreshCwIcon} title="Automatic claim tracking" description={`That address gets added to your ${payerName} account, so Granted can track new claims automatically.`} />
            <InfoRow icon={LockIcon} title="Private by design" description={`We only use this address for ${payerName}. You can remove it and delete your data at any time.`} />
          </Card>,
  primary: {
    label: "Yes, add new email",
    action: "continue"
  },
  secondary: {
    label: "Maybe later",
    action: "skip"
  }
}, {
  id: "waitlist",
  title: "You’re on the waitlist",
  subtitle: `The feature is currently in beta with ${payerName}. You are on the waitlist. We’ll let you know when it’s ready for you to enable.`,
  content: <YStack alignItems="center" justifyContent="center">
        <ResponsiveImage src={{
      web: "/static/billUploadSuccess.png",
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
      native: require("../../../assets/billUploadSuccess.png")
    }} alt="Bill upload success" width={isWeb ? 240 : 220} height={isWeb ? 240 : 220} />
      </YStack>,
  primary: {
    label: "Done",
    action: "done"
  }
}];
const thankYouStep: StepConfig = {
  id: "thank_you",
  title: "Thank you",
  content: <YStack flex={1} alignItems="center" justifyContent="center" gap="$sp4" marginTop="$sp10">
      <GrantedLogo width="$si11" height="$si11" />
      <Display5 textAlign="center">Thank you for your response</Display5>
    </YStack>
};
export const FakeDoorMmfaFlow = ({
  variant,
  payerName,
  onExit
}: {
  variant: FakeDoorMmfaVariant;
  payerName: string;
  onExit: () => void;
}) => {
  const steps = useMemo(() => getSteps(variant, payerName), [variant, payerName]);
  const {
    currentStep,
    progress,
    canGoBack,
    runAction,
    handleBack,
    handleDismiss
  } = useFakeDoorMmfa({
    steps,
    thankYouStep,
    variant,
    onExit
  });
  return <FakeDoorMmfaStep step={currentStep} progress={progress} canGoBack={canGoBack} onAction={runAction} onBack={handleBack} onDismiss={handleDismiss} data-sentry-element="FakeDoorMmfaStep" data-sentry-component="FakeDoorMmfaFlow" data-sentry-source-file="FakeDoorMmfaFlow.tsx" />;
};